/**
 * Google Calendar Authentication Service
 * 
 * Handles OAuth 2.0 authentication flow with Google Calendar API.
 * 
 * Security Principles:
 * - OAuth 2.0 only (no stored credentials)
 * - Tokens held in memory only during session
 * - Least-privilege scopes (read/write calendar events only)
 * - Explicit user consent required
 * - SOC2/GDPR compliant design
 * 
 * @module GoogleCalendarAuthService
 */

import type {
  OAuthTokens,
  TokenValidationResult,
  CalendarConnectionState,
  CalendarConnectionError,
  CalendarErrorCode,
  ConnectionResult,
  DisconnectResult,
  AsyncResult,
  ExternalCalendarInfo,
} from './types';
import {
  CALENDAR_SERVER_INTEGRATION_AVAILABLE,
  CALENDAR_SERVER_INTEGRATION_MESSAGE,
} from './calendarSecurityPolicy';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Google OAuth configuration.
 * Client ID should come from environment variables in production.
 */
interface GoogleOAuthConfig {
  clientId: string;
  redirectUri: string;
  scopes: string[];
}

/**
 * Default OAuth scopes for Google Calendar integration.
 * Using minimum required scopes for calendar access.
 */
const DEFAULT_SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
];

/**
 * Token expiration buffer in seconds.
 * Refresh tokens before they expire by this amount.
 */
const TOKEN_EXPIRY_BUFFER_SECONDS = 300; // 5 minutes

/**
 * Google OAuth endpoints.
 */
const GOOGLE_OAUTH_ENDPOINTS = {
  authorization: 'https://accounts.google.com/o/oauth2/v2/auth',
  token: 'https://oauth2.googleapis.com/token',
  revoke: 'https://oauth2.googleapis.com/revoke',
  userInfo: 'https://www.googleapis.com/oauth2/v2/userinfo',
  calendarList: 'https://www.googleapis.com/calendar/v3/users/me/calendarList',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a structured error object.
 */
function createError(
  code: CalendarErrorCode,
  message: string,
  details?: string,
  recoverable = true,
  suggestedAction?: string
): CalendarConnectionError {
  return {
    code,
    message,
    details,
    recoverable,
    suggestedAction,
    occurredAt: new Date().toISOString(),
  };
}

/**
 * Generates a cryptographically secure random string for OAuth state parameter.
 */
function generateStateParam(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates PKCE code verifier and challenge for secure OAuth flow.
 */
async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);
  const challenge = btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  return { verifier, challenge };
}

// ─────────────────────────────────────────────────────────────────────────────
// Google Calendar Auth Service Class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Service for managing Google Calendar OAuth authentication.
 * 
 * Usage:
 * ```typescript
 * const authService = GoogleCalendarAuthService.getInstance();
 * const result = await authService.connect();
 * if (result.success) {
 *   console.log('Connected:', result.data.state.userEmail);
 * }
 * ```
 */
export class GoogleCalendarAuthService {
  private static instance: GoogleCalendarAuthService | null = null;

  // In-memory token storage (never persisted to disk/localStorage for security)
  private tokens: OAuthTokens | null = null;

  // Current connection state
  private connectionState: CalendarConnectionState = {
    status: 'disconnected',
    provider: 'google',
  };

  // OAuth flow state (for popup callback verification)
  private pendingAuthState: string | null = null;
  private pendingCodeVerifier: string | null = null;

  // Configuration
  private config: GoogleOAuthConfig;

  // ─────────────────────────────────────────────────────────────────────────
  // Constructor & Singleton
  // ─────────────────────────────────────────────────────────────────────────

  private constructor() {
    // Configuration from environment (with fallbacks for development)
    this.config = {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      redirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`,
      scopes: DEFAULT_SCOPES,
    };
  }

  /**
   * Get singleton instance of the service.
   */
  public static getInstance(): GoogleCalendarAuthService {
    if (!GoogleCalendarAuthService.instance) {
      GoogleCalendarAuthService.instance = new GoogleCalendarAuthService();
    }
    return GoogleCalendarAuthService.instance;
  }

  /**
   * Reset singleton instance (for testing purposes).
   */
  public static resetInstance(): void {
    if (GoogleCalendarAuthService.instance) {
      GoogleCalendarAuthService.instance.clearTokens();
      GoogleCalendarAuthService.instance = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Connection Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Initiates Google Calendar OAuth connection flow.
   * Opens a popup for user authentication.
   * 
   * @returns Connection result with state
   */
  public async connect(): Promise<AsyncResult<ConnectionResult>> {
    if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) {
      return {
        success: false,
        error: createError(
          'AUTH_FAILED',
          CALENDAR_SERVER_INTEGRATION_MESSAGE,
          'Provider tokens must never be returned to the AX1 browser.',
          false,
          'Contact your administrator after the server-managed integration is deployed',
        ),
      };
    }

    try {
      // Validate configuration
      if (!this.config.clientId) {
        return {
          success: false,
          error: createError(
            'AUTH_FAILED',
            'Google OAuth not configured',
            'Missing VITE_GOOGLE_CLIENT_ID environment variable',
            false,
            'Contact your administrator to configure Google Calendar integration'
          ),
        };
      }

      // Update state to connecting
      this.updateConnectionState({ status: 'connecting' });

      // Generate PKCE and state for secure OAuth flow
      const { verifier, challenge } = await generatePKCE();
      const state = generateStateParam();

      this.pendingAuthState = state;
      this.pendingCodeVerifier = verifier;

      // Build authorization URL
      const authUrl = this.buildAuthorizationUrl(state, challenge);

      // Open popup for OAuth flow
      const authResult = await this.openAuthPopup(authUrl);

      if (!authResult.success) {
        this.updateConnectionState({
          status: 'error',
          error: authResult.error,
        });
        return authResult as AsyncResult<ConnectionResult>;
      }

      // Exchange authorization code for tokens
      const tokenResult = await this.exchangeCodeForTokens(authResult.data.code);

      if (!tokenResult.success) {
        this.updateConnectionState({
          status: 'error',
          error: tokenResult.error,
        });
        return {
          success: false,
          error: tokenResult.error,
        };
      }

      // Store tokens in memory
      this.tokens = tokenResult.data;

      // Fetch user info and calendar list
      const userInfoResult = await this.fetchUserInfo();
      const calendarsResult = await this.fetchCalendarList();

      // Update connection state with full info
      this.updateConnectionState({
        status: 'connected',
        userEmail: userInfoResult.success ? userInfoResult.data.email : undefined,
        userName: userInfoResult.success ? userInfoResult.data.name : undefined,
        connectedAt: new Date().toISOString(),
        lastTokenRefresh: new Date().toISOString(),
        availableCalendars: calendarsResult.success ? calendarsResult.data : [],
        error: undefined,
      });

      return {
        success: true,
        data: {
          connected: true,
          state: this.getConnectionState(),
        },
      };
    } catch (error) {
      const connectionError = createError(
        'UNKNOWN_ERROR',
        'Failed to connect to Google Calendar',
        error instanceof Error ? error.message : String(error),
        true,
        'Please try again'
      );

      this.updateConnectionState({
        status: 'error',
        error: connectionError,
      });

      return {
        success: false,
        error: connectionError,
      };
    } finally {
      // Clear pending auth state
      this.pendingAuthState = null;
      this.pendingCodeVerifier = null;
    }
  }

  /**
   * Disconnects from Google Calendar and revokes tokens.
   * 
   * @returns Disconnect result
   */
  public async disconnect(): Promise<AsyncResult<DisconnectResult>> {
    try {
      let tokensRevoked = false;
      const warnings: string[] = [];

      // Attempt to revoke tokens at Google
      if (this.tokens?.accessToken) {
        const revokeResult = await this.revokeTokens();
        tokensRevoked = revokeResult.success;
        if (!revokeResult.success) {
          warnings.push('Could not revoke tokens at Google. They will expire naturally.');
        }
      }

      // Clear local state regardless of revoke result
      this.clearTokens();
      this.updateConnectionState({
        status: 'disconnected',
        userEmail: undefined,
        userName: undefined,
        connectedAt: undefined,
        lastTokenRefresh: undefined,
        availableCalendars: undefined,
        error: undefined,
      });

      return {
        success: true,
        data: {
          disconnected: true,
          tokensRevoked,
          warnings: warnings.length > 0 ? warnings : undefined,
        },
      };
    } catch (error) {
      // Even on error, clear local state for security
      this.clearTokens();
      this.updateConnectionState({ status: 'disconnected' });

      return {
        success: false,
        error: createError(
          'UNKNOWN_ERROR',
          'Error during disconnect',
          error instanceof Error ? error.message : String(error),
          true
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Token Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Validates the current access token.
   * 
   * @returns Token validation result
   */
  public validateToken(): TokenValidationResult {
    if (!this.tokens) {
      return {
        isValid: false,
        isExpired: true,
        isExpiringSoon: false,
        secondsUntilExpiry: 0,
        error: 'No tokens available',
      };
    }

    const now = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = this.tokens.expiresAt - now;
    const isExpired = secondsUntilExpiry <= 0;
    const isExpiringSoon = secondsUntilExpiry <= TOKEN_EXPIRY_BUFFER_SECONDS;

    return {
      isValid: !isExpired,
      isExpired,
      isExpiringSoon,
      secondsUntilExpiry,
    };
  }

  /**
   * Gets a valid access token, refreshing if necessary.
   * 
   * @returns Valid access token or error
   */
  public async getValidAccessToken(): Promise<AsyncResult<string>> {
    if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) {
      return {
        success: false,
        error: createError(
          'AUTH_FAILED',
          CALENDAR_SERVER_INTEGRATION_MESSAGE,
          'Browser access to provider tokens is disabled.',
          false,
        ),
      };
    }

    const validation = this.validateToken();

    if (validation.isExpired) {
      // Attempt to refresh
      const refreshResult = await this.refreshAccessToken();
      if (!refreshResult.success) {
        this.updateConnectionState({
          status: 'token_expired',
          error: refreshResult.error,
        });
        return refreshResult;
      }
    } else if (validation.isExpiringSoon && this.tokens?.refreshToken) {
      // Proactively refresh token that's expiring soon
      // Don't await - let it happen in background
      this.refreshAccessToken().catch(console.error);
    }

    if (!this.tokens?.accessToken) {
      return {
        success: false,
        error: createError(
          'TOKEN_EXPIRED',
          'No valid access token available',
          undefined,
          true,
          'Please reconnect your Google Calendar'
        ),
      };
    }

    return {
      success: true,
      data: this.tokens.accessToken,
    };
  }

  /**
   * Refreshes the access token using the refresh token.
   * 
   * @returns New access token or error
   */
  public async refreshAccessToken(): Promise<AsyncResult<string>> {
    if (!this.tokens?.refreshToken) {
      return {
        success: false,
        error: createError(
          'TOKEN_REFRESH_FAILED',
          'No refresh token available',
          'User must re-authenticate',
          true,
          'Please reconnect your Google Calendar'
        ),
      };
    }

    try {
      // In production, this would call our backend to refresh tokens
      // The backend handles the client_secret securely
      const response = await fetch('/api/auth/google/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: this.tokens.refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();

      // Update stored tokens
      this.tokens = {
        ...this.tokens,
        accessToken: data.access_token,
        expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
      };

      this.updateConnectionState({
        lastTokenRefresh: new Date().toISOString(),
        status: 'connected',
        error: undefined,
      });

      return {
        success: true,
        data: this.tokens.accessToken,
      };
    } catch (error) {
      const refreshError = createError(
        'TOKEN_REFRESH_FAILED',
        'Failed to refresh access token',
        error instanceof Error ? error.message : String(error),
        true,
        'Please reconnect your Google Calendar'
      );

      return {
        success: false,
        error: refreshError,
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: State Access
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Gets the current connection state.
   */
  public getConnectionState(): CalendarConnectionState {
    return { ...this.connectionState };
  }

  /**
   * Checks if currently connected to Google Calendar.
   */
  public isConnected(): boolean {
    return CALENDAR_SERVER_INTEGRATION_AVAILABLE
      && this.connectionState.status === 'connected'
      && this.tokens !== null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: OAuth Flow
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Builds the Google OAuth authorization URL.
   */
  private buildAuthorizationUrl(state: string, codeChallenge: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scopes.join(' '),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      access_type: 'offline', // Request refresh token
      prompt: 'consent', // Always show consent screen for refresh token
    });

    return `${GOOGLE_OAUTH_ENDPOINTS.authorization}?${params.toString()}`;
  }

  /**
   * Opens OAuth popup and waits for callback.
   */
  private async openAuthPopup(
    authUrl: string
  ): Promise<AsyncResult<{ code: string }>> {
    return new Promise((resolve) => {
      // Popup dimensions and position
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'google-oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      if (!popup) {
        resolve({
          success: false,
          error: createError(
            'POPUP_BLOCKED',
            'Popup was blocked by the browser',
            'Please allow popups for this site and try again',
            true,
            'Enable popups in your browser settings'
          ),
        });
        return;
      }

      // Listen for callback message from popup
      const handleMessage = (event: MessageEvent) => {
        // Verify origin
        if (event.origin !== window.location.origin) return;

        const { type, code, state, error } = event.data || {};

        if (type !== 'google-oauth-callback') return;

        // Cleanup
        window.removeEventListener('message', handleMessage);
        clearInterval(pollTimer);
        popup.close();

        // Verify state parameter
        if (state !== this.pendingAuthState) {
          resolve({
            success: false,
            error: createError(
              'AUTH_FAILED',
              'OAuth state mismatch',
              'Security verification failed. Please try again.',
              true
            ),
          });
          return;
        }

        if (error) {
          const errorCode: CalendarErrorCode =
            error === 'access_denied' ? 'USER_CANCELLED' : 'AUTH_FAILED';
          resolve({
            success: false,
            error: createError(
              errorCode,
              error === 'access_denied'
                ? 'User cancelled the authorization'
                : 'Authorization failed',
              error,
              errorCode === 'USER_CANCELLED'
            ),
          });
          return;
        }

        if (code) {
          resolve({
            success: true,
            data: { code },
          });
        }
      };

      window.addEventListener('message', handleMessage);

      // Poll for popup closure (user closed without completing)
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          resolve({
            success: false,
            error: createError(
              'USER_CANCELLED',
              'Authorization window was closed',
              undefined,
              true
            ),
          });
        }
      }, 500);
    });
  }

  /**
   * Exchanges authorization code for tokens via backend.
   */
  private async exchangeCodeForTokens(
    code: string
  ): Promise<AsyncResult<OAuthTokens>> {
    try {
      // Call backend to exchange code (backend has client_secret)
      const response = await fetch('/api/auth/google/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          codeVerifier: this.pendingCodeVerifier,
          redirectUri: this.config.redirectUri,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Token exchange failed: ${response.status}`);
      }

      const data = await response.json();

      const tokens: OAuthTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
        tokenType: data.token_type || 'Bearer',
        scopes: (data.scope || '').split(' ').filter(Boolean),
      };

      return {
        success: true,
        data: tokens,
      };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'AUTH_FAILED',
          'Failed to exchange authorization code',
          error instanceof Error ? error.message : String(error),
          true,
          'Please try again'
        ),
      };
    }
  }

  /**
   * Revokes tokens at Google.
   */
  private async revokeTokens(): Promise<AsyncResult<void>> {
    if (!this.tokens?.accessToken) {
      return { success: true, data: undefined };
    }

    try {
      const response = await fetch(
        `${GOOGLE_OAUTH_ENDPOINTS.revoke}?token=${this.tokens.accessToken}`,
        { method: 'POST' }
      );

      if (!response.ok && response.status !== 400) {
        // 400 means token already invalid, which is fine
        throw new Error(`Revoke failed: ${response.status}`);
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to revoke tokens',
          error instanceof Error ? error.message : String(error),
          true
        ),
      };
    }
  }

  /**
   * Fetches user info from Google.
   */
  private async fetchUserInfo(): Promise<
    AsyncResult<{ email: string; name: string }>
  > {
    if (!this.tokens?.accessToken) {
      return {
        success: false,
        error: createError('AUTH_FAILED', 'No access token available'),
      };
    }

    try {
      const response = await fetch(GOOGLE_OAUTH_ENDPOINTS.userInfo, {
        headers: {
          Authorization: `Bearer ${this.tokens.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user info: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        data: {
          email: data.email,
          name: data.name || data.email,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to fetch user info',
          error instanceof Error ? error.message : String(error)
        ),
      };
    }
  }

  /**
   * Fetches list of calendars from Google.
   */
  private async fetchCalendarList(): Promise<AsyncResult<ExternalCalendarInfo[]>> {
    if (!this.tokens?.accessToken) {
      return {
        success: false,
        error: createError('AUTH_FAILED', 'No access token available'),
      };
    }

    try {
      const response = await fetch(GOOGLE_OAUTH_ENDPOINTS.calendarList, {
        headers: {
          Authorization: `Bearer ${this.tokens.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch calendars: ${response.status}`);
      }

      const data = await response.json();

      const calendars: ExternalCalendarInfo[] = (data.items || []).map(
        (cal: Record<string, unknown>) => ({
          id: cal.id as string,
          name: cal.summary as string,
          isPrimary: cal.primary === true,
          color: cal.backgroundColor as string | undefined,
          accessRole: cal.accessRole as 'owner' | 'writer' | 'reader',
          syncEnabled: false, // Default to disabled, user must enable
        })
      );

      return {
        success: true,
        data: calendars,
      };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to fetch calendar list',
          error instanceof Error ? error.message : String(error)
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: State Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Updates connection state.
   */
  private updateConnectionState(
    updates: Partial<CalendarConnectionState>
  ): void {
    this.connectionState = {
      ...this.connectionState,
      ...updates,
    };
  }

  /**
   * Clears all stored tokens.
   */
  private clearTokens(): void {
    this.tokens = null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience Functions (Facade)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Connects to Google Calendar.
 * Convenience function that uses the singleton service instance.
 * 
 * @returns Connection result
 */
export async function connectGoogleCalendar(): Promise<AsyncResult<ConnectionResult>> {
  const service = GoogleCalendarAuthService.getInstance();
  return service.connect();
}

/**
 * Disconnects from Google Calendar.
 * Convenience function that uses the singleton service instance.
 * 
 * @returns Disconnect result
 */
export async function disconnectGoogleCalendar(): Promise<AsyncResult<DisconnectResult>> {
  const service = GoogleCalendarAuthService.getInstance();
  return service.disconnect();
}

/**
 * Gets the current Google Calendar connection state.
 * 
 * @returns Current connection state
 */
export function getGoogleCalendarConnectionState(): CalendarConnectionState {
  const service = GoogleCalendarAuthService.getInstance();
  return service.getConnectionState();
}

/**
 * Checks if connected to Google Calendar.
 * @returns True if connected
 */
export function isGoogleCalendarConnected(): boolean {
  if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) return false;
  const service = GoogleCalendarAuthService.getInstance();
  return service.isConnected();
}

// Default export for the service class
export default GoogleCalendarAuthService;
